import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  refreshToken,
  login as loginApi,
  register as registerApi,
  logout as logoutApi,
} from "@/api/auth";
import type { LoginRequest, RegisterRequest } from "@/api/auth";

interface User {
  id: string;
  email: string;
  plan: string;
  billingCycle: string;
  createdAt: string;
  emailVerifiedAt: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function loadStoredUser(): User | null {
  try {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function storeUser(user: User | null) {
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(loadStoredUser);
  const [isLoading, setIsLoading] = useState(!loadStoredUser());

  const refreshUser = useCallback(async () => {
    try {
      const response = await refreshToken();
      setUser(response.user);
      storeUser(response.user);
    } catch {
      setUser(null);
      storeUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = async (data: LoginRequest) => {
    const response = await loginApi(data);
    setUser(response.user);
    storeUser(response.user);
  };

  const register = async (data: RegisterRequest) => {
    const response = await registerApi(data);
    setUser(response.user);
    storeUser(response.user);
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
    storeUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
